import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import redial from 'redial'
import { Helmet } from 'react-helmet'
import classNames from 'classnames'
import Loading from '../../components/util/Loading'
import './weather.pcss'
import { fetchWeather } from './weatherActions'
import { MobileListAd, BOX_AD_CONTENT_1 } from '../../ad/Ad'
import { selectIsUserLoggedIn, selectUserDefaultWeatherLocationId, selectUserWeatherLocations } from '../../selectors/userSelector'
import { clearLocationSearch } from '../../components/location/locationActions'
import LocationSearchDialog from '../../components/location/LocationSearchDialog'
import CurrentWeather from './CurrentWeather'
import GeneralSidebar from '../../components/layout/GeneralSidebar'
import UnableToLoad from '../../components/messages/UnableToLoad'
import {
  addUserWeatherLocation,
  removeUserWeatherLocation,
  setUserDefaultWeatherLocation,
} from '../../user/userActions'
import { setStatus } from '../../status/statusActions'
import MainContent from '../../components/wrappers/MainContent'
import { createUrl } from '../../lib/utils'
import shareImg from '../../assets/ampparit-share-saa.jpg'
import PropTypes from 'prop-types'
import SidebarMenu from '../../components/layout/SidebarMenu'
import Star from './icons/star'
import { genericNotification } from '../../components/notification/notificationActions'
import {
  selectWeatherLoading,
  selectWeatherFailedToLoad,
  selectCurrentLocationId,
  selectCurrentLocationName,
  selectCurrentLocationData,
} from '../../selectors/weatherSelectors'
import WeatherFavoriteLocations from './WeatherFavoriteLocations'
import WeeklyWeather from './WeeklyWeather'

const WeatherView = ({ path }) => {
  const dispatch = useDispatch()
  const [displayFavoriteLocations, setDisplayFavoriteLocations] = useState(false)

  const loading = useSelector(selectWeatherLoading)
  const failedToLoad = useSelector(selectWeatherFailedToLoad)
  const currentLocationId = useSelector(selectCurrentLocationId)
  const currentLocationName = useSelector(selectCurrentLocationName)
  const currentLocationData = useSelector(selectCurrentLocationData)
  const userLocations = useSelector(selectUserWeatherLocations)
  const userDefaultLocationId = useSelector(selectUserDefaultWeatherLocationId)
  const loggedIn = useSelector(selectIsUserLoggedIn)

  const isWeatherFrontPage = path.split('/').filter(part => part.length > 0).length === 1

  const title = isWeatherFrontPage ? 'Sää' : 'Sää ' + currentLocationName
  const description = isWeatherFrontPage
    ? 'Ampparit.comin sääpalvelusta löydät paikkakuntakohtaiset säätiedot ja ennusteet. Tarkista helposti, miltä 10 vrk sää näyttää paikkakunnallasi.'
    : 'Ampparit.comin sääpalvelusta löydät paikkakuntakohtaiset säätiedot ja ennusteet. Tarkista helposti, miltä paikkakunnan ' + currentLocationName + ' 10 vrk sää näyttää.'
  const forecasts = currentLocationData.take(2).flatMap(d => d.get('forecasts'))
  const isCurrentFavorite = userLocations.some(loc => loc.get('id') === currentLocationId)
  const isCurrentDefault = currentLocationId === userDefaultLocationId

  const handleLocationSelect = (result) => {
    if (result) {
      const locationSlug = result.get('slug')
      dispatch(push(`/saa/${locationSlug}`))
      dispatch(clearLocationSearch())
      setDisplayFavoriteLocations(false)
    }
  }

  const handleFavoriteClick = (e) => {
    const clickedLocationId = parseInt(e.currentTarget.dataset.locationId, 10)
    const clickedLocationSlug = e.currentTarget.dataset.locationSlug
    const isLocationInFavorites = userLocations.some(loc => loc.get('id') === clickedLocationId)
    const removalAllowed = clickedLocationId !== userDefaultLocationId

    if (!loggedIn) {
      dispatch(genericNotification(
        'Kirjaudu sisään lisätäksesi suosikkeihin',
        'info',
        'FavoriteClick'
      ))
    } else if (!isLocationInFavorites) {
      dispatch(addUserWeatherLocation(clickedLocationId))
      if (clickedLocationSlug) {
        dispatch(push(`/saa/${clickedLocationSlug}`))
        dispatch(clearLocationSearch())
      }
    } else if (removalAllowed) {
      dispatch(removeUserWeatherLocation(clickedLocationId))
    } else {
      dispatch(genericNotification(
        'Oletussijaintia ei voi poistaa suosikeista. Valitse ensin toinen oletussijainti.',
        'error',
        'FavoriteClick'
      ))
    }
  }

  const handleDisplayFavoriteLocations = () => {
    if (loggedIn) {
      setDisplayFavoriteLocations(prev => !prev)
    } else {
      dispatch(genericNotification(
        'Kirjaudu sisään nähdäksesi suosikkisi',
        'info',
        'DisplayFavoriteLocations'
      ))
    }
  }

  const handleSetDefaultClick = (e) => {
    const locationId = parseInt(e.currentTarget.dataset.locationId, 10)
    dispatch(setUserDefaultWeatherLocation(locationId))
  }

  return (
    <div className='container' id='sticky-ad-bottom-boundary'>
      <div className='main-container'>
        <SidebarMenu />
        <MainContent>
          <Helmet
            title={ title }
            meta={ [
              {name: 'description', content: description},
              {property: 'og:title', content: 'Sää \u2013 Ampparit.com'},
              {property: 'og:image', content: createUrl(shareImg)},
              {property: 'og:description', content: 'Ampparit.comin sääpalvelusta löydät paikkakuntakohtaiset säätiedot ja ennusteet. Tarkista helposti, miltä 10 vrk sää näyttää paikkakunnallasi! Palvelusta näet myös tuntikohtaisen ennusteen, sademäärät ja ilmankosteuden tiedot.'},
            ] }
          />

          <MobileListAd adUnitId={ BOX_AD_CONTENT_1 } />

          <div className='weather-title'>
            <h1>{ loading ? 'Ladataan säätietoja...' : title }</h1>

            <div className='weather-title__actions'>
              <button
                type='button'
                className='weather-title__actions-button'
                onClick={ handleDisplayFavoriteLocations }
                aria-label={ displayFavoriteLocations ? 'Piilota suosikkisijainnit' : 'Näytä suosikkisijaintien lista' }
                title={ displayFavoriteLocations ? 'Piilota suosikkisijainnit' : 'Näytä suosikkisijaintien lista' }
              >
                <Star className={ classNames('weather-title__actions-icon', {'display-favorites': displayFavoriteLocations}) } />
                <span className='weather-title__actions-title'>Suosikit</span>
              </button>
            </div>
          </div>

          <LocationSearchDialog
            onLocationSelect={ handleLocationSelect }
            onFavoriteClick={ handleFavoriteClick }
          />

          { displayFavoriteLocations && loggedIn &&
            <WeatherFavoriteLocations
              currentLocationId={ currentLocationId }
              currentLocationName={ currentLocationName }
              isCurrentFavorite={ isCurrentFavorite }
              isCurrentDefault={ isCurrentDefault }
              userLocations={ userLocations }
              userDefaultLocationId={ userDefaultLocationId }
              onSetDefault={ handleSetDefaultClick }
              onFavoriteClick={ handleFavoriteClick }
            />
          }

          <div className='weather-container'>
            <Loading loading={ loading }>
              <div className={ classNames('weather-main-container', { 'weather-loading': loading } ) }>

                { failedToLoad ? (
                  <UnableToLoad errorText='Säätietoja ei saatu ladattua' />
                ) : (
                  <CurrentWeather
                    forecasts={ forecasts }
                    location={ currentLocationName }
                    locationId={ currentLocationId }
                    onFavoriteClick={ handleFavoriteClick }
                    isFavorite={ isCurrentFavorite }
                    loggedIn={ loggedIn }
                  />
                ) }
              </div>
            </Loading>
          </div>

          { !forecasts.isEmpty() && !failedToLoad &&
            <WeeklyWeather
              loading={ loading }
              locationData={ currentLocationData }
            />
          }

          <p className='weather-data-from'>Säädata © Foreca</p>
        </MainContent>

        <div className='sidebar-container'>
          <GeneralSidebar />
        </div>
      </div>
    </div>
  )
}

// eslint-disable-next-line react/prefer-exact-props
WeatherView.propTypes = {
  path: PropTypes.string.isRequired,
}

export default redial.provideHooks({
  fetch: ({dispatch, getState, path}) => {

    // Location can be specified by URL slug. Bail out if slug is obviously invalid
    const pathEnding = path.split('/').filter(part => part.length > 0).pop()
    const locationSlug = path.startsWith('/saa') && pathEnding !== 'saa' ? pathEnding : null
    if (locationSlug && !locationSlug.match(/^[a-z0-9-]+$/)) {
      dispatch(setStatus(404))
      return
    }

    // When location has not been specified by URL, use user's default location ID
    const defaultLocationId = selectUserDefaultWeatherLocationId(getState())

    // fetchWeather data. Redirect to 404 if fetch failed.
    return Promise.all([
      dispatch(fetchWeather(locationSlug || defaultLocationId)).then((statusCode) => {
        if (statusCode === 404) {
          return dispatch(setStatus(404))
        }
      }),
    ])
  },
})(WeatherView)
